import { Component, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CuiButtonComponent, CuiDialogContainerComponent, CuiInputComponent, CuiPasswordComponent } from '@idealink-material/ui-core';
import { AuthService } from '@/core/service/auth.service';
import { AES } from '@/core/service/aes';
import { MsgService } from '@/core/service/msg.service';
import { SessionMonitorService } from '@/core/service/session-monitor.service';
import { STORAGE_KEYS } from '@/share/constant/storage_token.key';
import { STORAGE_SERVICE_TOKEN } from '@/core/util/storage.token';
import { SessionStatus } from '@/core/model/session-status';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-session-duration-locked',
    template: `
        <p-dialog-container #dialogContainer [title]="'session.timeout.title' | translate" [showClose]="true" [dividers]="false" (closed)="closed()">
            <!-- Message -->
            <div class="text-center mt-6 mb-6 px-4">
                <p>{{ 'session.timeout.message' | translate }}</p>
            </div>

            <!-- Form -->
            <form [formGroup]="form" (ngSubmit)="login()" class="px-4 pb-4">
                <div class="flex flex-col gap-4 w-full">
                    <div class="flex flex-col gap-2">
                        <label for="address"> {{ 'session.timeout.email' | translate }}</label>
                        <p-input id="address" type="text" formControlName="username" [fullWidth]="true" />
                    </div>
                    <div class="flex flex-col gap-2">
                        <label for="password"> {{ 'session.timeout.password' | translate }}</label>
                        <p-password id="password" formControlName="password" placeholder="Password" [toggleMask]="true" [fullWidth]="true" [feedback]="false"></p-password>
                    </div>

                    <div class="mt-3">
                        <p-button [loading]="loading()" [fullWidth]="true" (onClick)="login()">{{ 'session.timeout.login' | translate }}</p-button>
                    </div>
                </div>
            </form>
        </p-dialog-container>
    `,
    styleUrl: './custom-dialog.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CuiButtonComponent, CuiDialogContainerComponent, CuiInputComponent, CuiPasswordComponent, ReactiveFormsModule, TranslatePipe]
})
export class SessionDurationLocked {
    @ViewChild('dialogContainer') dialogContainer!: CuiDialogContainerComponent;
    form!: FormGroup;
    loading = signal(false);
    private storageService = inject(STORAGE_SERVICE_TOKEN);

    constructor(
        private auth: AuthService,
        private aes: AES,
        private msgService: MsgService,
        public sessionMonitor: SessionMonitorService,
        private formBuilder: FormBuilder
    ) {
        this.initForm();
    }

    private initForm() {
        this.form = this.formBuilder.group({
            username: [{ value: 'super-admin', disabled: true }, Validators.required],
            password: [null, Validators.required],
            rememberMe: false
        });
    }

    login(): void {
        this.loading.set(true);
        // const encryptedPassword = this.aes.encryptGcm(this.password());

        const payload = {
            username: 'super-admin',
            password: 'super@123',
            rememberMe: true
        };

        this.auth.login(payload).subscribe({
            next: (res: any) => {
                this.loading.set(false);
                if (res.response.code === 200) {
                    this.storageService.set(STORAGE_KEYS.TOKEN, res.results.token);
                    this.storageService.set(STORAGE_KEYS.TOKEN, res.results.refreshToken);
                    this.sessionMonitor.initTimer();
                    this.closeDialog();
                } else {
                    this.msgService.error('Login Failed', res.response.message);
                }
            },
            error: (err: any) => {
                this.loading.set(false);
                this.msgService.error('Exception', err.message);
            }
        });
    }

    closed(): void {
        this.closeDialog();
        this.sessionMonitor.setStatus(SessionStatus.Active);
        this.auth.callLogout().subscribe({
            next: () => this.auth.logout(),
            error: () => this.auth.logout()
        });
    }

    private closeDialog(): void {
        this.sessionMonitor.destroy();
        this.resetForm();
        this.dialogContainer.close();
    }

    private resetForm(): void {
        this.loading.set(false);
    }
}
